/* see docs/EXTRACTED_COMMENTS.md#c200 */

import type {Component, ComponentList} from "../types.ts";

export function parsePx(value?: string): number {
    if (!value) return 0;
    return parseInt(`${(value || "")}`.replace("px", "").replace("100%", "0")) || 0;
}

// see docs/EXTRACTED_COMMENTS.md#c746
export const WORST_CASE_ROW_HEIGHT_PX = 20;

/* see docs/EXTRACTED_COMMENTS.md#c753 */
const MAX_SIZING_WALK = 32;

/* see docs/EXTRACTED_COMMENTS.md#c753 */
export function autoHeightMode(autoSize?: string): "parent" | "child" | "none" {
    if (!autoSize || autoSize === "AUTOSIZE_NONE") return "none";
    if (autoSize.includes("PARENT_HEIGHT")) return "parent";
    if (autoSize.includes("CHILD_HEIGHT")) return "child";
    return "none";
}

/* see docs/EXTRACTED_COMMENTS.md#c754 */
function sizedHeight(
    item: Component | undefined,
    components: ComponentList | undefined,
    bound: "min" | "max",
    visiting: Set<string>,
): number {
    if (!item) return 0;
    const authored = parsePx(item.style?.height);
    const id = item.props?.id ?? "";
    const mode = autoHeightMode(item.auto_size);
    if (mode === "none" || visiting.has(id) || visiting.size >= MAX_SIZING_WALK) return authored;

    visiting.add(id);
    /* see docs/EXTRACTED_COMMENTS.md#c754 */
    const derived = mode === "parent"
        ? containerRoom(item, components, visiting)
        : childExtent(item, components, bound, visiting);
    visiting.delete(id);

    return derived > 0 ? derived + authored : authored;
}

/* see docs/EXTRACTED_COMMENTS.md#c754 */
function childExtent(
    item: Component,
    components: ComponentList | undefined,
    bound: "min" | "max",
    visiting: Set<string>,
): number {
    let extent = bound === "max" ? 0 : Infinity;
    for (const child of item.children ?? []) {
        const edge = parsePx(child.style?.top) + sizedHeight(child, components, bound, visiting);
        extent = bound === "max" ? Math.max(extent, edge) : Math.min(extent, edge);
    }
    return Number.isFinite(extent) ? extent : 0;
}

/* see docs/EXTRACTED_COMMENTS.md#c754 */
export function containerRoom(
    item?: Component,
    components?: ComponentList,
    visiting: Set<string> = new Set(),
): number {
    const parentId = getParent(item?.props?.id ?? "");
    const parent = parentId != null ? components?.[parentId] : undefined;
    if (!parent) return 0;
    return sizedHeight(parent, components, "max", visiting) - parsePx(item?.style?.top);
}

/* see docs/EXTRACTED_COMMENTS.md#c753 */
export function worstCaseHeight(item?: Component, components?: ComponentList): number {
    return sizedHeight(item, components, "max", new Set());
}

/* see docs/EXTRACTED_COMMENTS.md#c754 */
export function leastHeight(item?: Component, components?: ComponentList): number {
    return sizedHeight(item, components, "min", new Set());
}

/* see docs/EXTRACTED_COMMENTS.md#c753 */
export function datasheetHeight(sheet?: Component, components?: ComponentList): number {
    if (!sheet) return 0;
    /* see docs/EXTRACTED_COMMENTS.md#c754 */
    const derived = autoHeightMode(sheet.auto_size) === "child"
        ? Math.max(0, containerRoom(sheet, components)) + parsePx(sheet.style?.height)
        : worstCaseHeight(sheet, components);
    /* see docs/EXTRACTED_COMMENTS.md#c753 */
    return derived > 0 ? derived : parsePx(sheet.props?.style?.height);
}

/* see docs/EXTRACTED_COMMENTS.md#c754 */
export function datasheetRowDivisor(row?: Component, components?: ComponentList): number {
    if (!row) return 0;
    const authored = parsePx(row.style?.height);
    /* see docs/EXTRACTED_COMMENTS.md#c747 */
    if (autoHeightMode(row.auto_size) === "none") {
        /* see docs/EXTRACTED_COMMENTS.md#c746 */
        return authored > 0 ? authored : WORST_CASE_ROW_HEIGHT_PX;
    }
    /* see docs/EXTRACTED_COMMENTS.md#c754 */
    return Math.max(leastHeight(row, components), WORST_CASE_ROW_HEIGHT_PX);
}

/* see docs/EXTRACTED_COMMENTS.md#c580 */
export function datasheetRowCapacity(sheet?: Component, components?: ComponentList): number {
    /* see docs/EXTRACTED_COMMENTS.md#c753 */
    const sheetHeight = datasheetHeight(sheet, components);
    if (sheetHeight <= 0) return 0;
    const row = sheet?.children?.[0];
    if (!row) return 0;
    /* see docs/EXTRACTED_COMMENTS.md#c753 */
    if (autoHeightMode(row.auto_size) === "parent") return 1;
    /* see docs/EXTRACTED_COMMENTS.md#c738 */
    return Math.max(1, Math.floor(sheetHeight / datasheetRowDivisor(row, components)));
}

/* see docs/EXTRACTED_COMMENTS.md#c749 */
export function reportableRowCapacity(sheet?: Component): number {
    const sheetHeight = parsePx(sheet?.props?.style?.height);
    if (sheetHeight <= 0) return 0;
    const row = sheet?.children?.[0];
    if (!row) return 0;
    /* see docs/EXTRACTED_COMMENTS.md#c753 */
    const mode = autoHeightMode(row.auto_size);
    if (mode === "parent") return 1;
    if (mode === "child") return 0;
    const rowHeight = parsePx(row.style?.height);
    if (rowHeight <= 0) return 0;
    return Math.max(1, Math.floor(sheetHeight / rowHeight));
}

/* see docs/EXTRACTED_COMMENTS.md#c750 */
export function datasheetChannel(sheet?: Component): number | null {
    if (!sheet?.dynamic) return null;
    const own = sheet.dynamic.key_channel ?? sheet.dynamic.channel;
    if (own) return own;
    const first = sheet.children?.[0]?.dynamic;
    if (!first) return null;
    return (first.key_channel ?? first.channel) || null;
}

/* see docs/EXTRACTED_COMMENTS.md#c764 */
export function datasheetAuthoredStart(sheet?: Component): number | null {
    const row = sheet?.dynamic?.original_object?.row;
    if (row == null) return null;
    const n = Number(row);
    return Number.isFinite(n) && n >= 0 ? n : null;
}

export type ChainMember = {
    id: string;
    channel: number | null;
    visible: boolean;
    supported: number;
};

/* see docs/EXTRACTED_COMMENTS.md#c750 */
export function chainStartRows(
    members: ChainMember[],
    clusterChannel: number,
    rowStart: number,
): Map<string, number> {
    const byChannel = new Map<number | null, ChainMember[]>();
    for (const member of members) {
        const group = byChannel.get(member.channel);
        if (group) group.push(member);
        else byChannel.set(member.channel, [member]);
    }

    const starts = new Map<string, number>();
    for (const [channel, group] of byChannel) {
        group.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
        let cursor = channel != null && channel === clusterChannel ? rowStart : 0;
        for (const member of group) {
            if (!member.visible) continue;
            starts.set(member.id, cursor);
            cursor += Math.max(0, member.supported);
        }
    }
    return starts;
}

// A root id has no parent, so return null rather than "". The old form leaned on
// `arr.length == 0`, which String.split can never produce, so a dotless id yielded ""
// — and "" is its own parent, which hung the `omit_duplicate` ancestor walk in
// Renderer.buildDedupGroups. See docs/design-history/client/LAYOUT_SERVE_SILENT_PARK_ANALYSIS.md.
export const getParent = (id: string): string | null => {
    if (id == null) return null;
    const idx = id.lastIndexOf('.');
    return idx === -1 ? null : id.slice(0, idx);
}
